/**
 * Outlook slice for climate projections management
 * Manages different time horizons for climate data visualization
 * Migrated from Gatsby legacy application
 */
import { createSlice } from '@reduxjs/toolkit';
import defaultOutlooksData from '../../../data/default-outlooks.json' with { type: 'json' };

/**
 * Default climate outlook periods with their corresponding warming levels
 * These values are loaded from data/default-outlooks.json and can be modified
 * by non-developers without touching the code
 * @constant {Array<Object>} DEFAULT_OUTLOOKS
 * @property {number} value - Year of the outlook
 * @property {string} rwl - Regional Warming Level
 * @property {string} gwl - Global Warming Level
 */
export const DEFAULT_OUTLOOKS = defaultOutlooksData;

const initialIndex = 1; // Default to 2050

const initialState = {
  index: initialIndex,
  outlooks: DEFAULT_OUTLOOKS,
  ...DEFAULT_OUTLOOKS[initialIndex],
};

/**
 * Outlook slice with reducers for climate projection management
 */
export const outlookSlice = createSlice({
  name: 'outlook',
  initialState,
  reducers: {
    /**
     * Change the current outlook period
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload containing index
     * @param {number} action.payload.index - Index of the outlook to select
     */
    changeOutlook: (state, action) => {
      const { index } = action.payload;
      
      if (index >= 0 && index < state.outlooks.length) {
        state.index = index;
        state.value = state.outlooks[index].value;
        state.rwl = state.outlooks[index].rwl;
        state.gwl = state.outlooks[index].gwl;
      }
    },
    
    /**
     * Reset outlook to default (2050) with default values
     * @param {Object} state - Current state
     */
    resetOutlook: (state) => {
      state.index = initialIndex;
      state.outlooks = DEFAULT_OUTLOOKS;
      state.value = DEFAULT_OUTLOOKS[initialIndex].value;
      state.rwl = DEFAULT_OUTLOOKS[initialIndex].rwl;
      state.gwl = DEFAULT_OUTLOOKS[initialIndex].gwl;
    },

    /**
     * Set entity-specific outlook values (for overseas territories)
     * Merges entity horizons with default outlooks
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload containing entity horizons
     * @param {Array<string>} [action.payload.horizons] - Array of RWL values for each outlook
     */
    setEntityOutlooks: (state, action) => {
      const { horizons } = action.payload;
      
      // If no entity-specific horizons, reset to defaults
      if (!horizons || !Array.isArray(horizons) || horizons.length === 0) {
        state.outlooks = DEFAULT_OUTLOOKS;
      } else {
        // Merge entity-specific RWL values with default outlooks
        state.outlooks = DEFAULT_OUTLOOKS.map((outlook, idx) => ({
          ...outlook,
          rwl: horizons[idx] || outlook.rwl,
        }));
      }
      
      // Update current outlook values
      const currentOutlook = state.outlooks[state.index];
      state.value = currentOutlook.value;
      state.rwl = currentOutlook.rwl;
      state.gwl = currentOutlook.gwl;
    },
  },
});

// Action creators are generated for each case reducer function
export const { changeOutlook, resetOutlook, setEntityOutlooks } = outlookSlice.actions;

// Selectors
export const selectOutlook = (state) => state.outlook;
export const selectOutlookIndex = (state) => state.outlook.index;
export const selectOutlookValue = (state) => state.outlook.value;
export const selectOutlookRwl = (state) => state.outlook.rwl;
export const selectOutlookGwl = (state) => state.outlook.gwl;
export const selectOutlooks = (state) => state.outlook.outlooks;

export default outlookSlice.reducer;
